import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BioentitySearchInputWithItems} from 'inventory/BioentitySearchInput';
import {SelectorOptionInput} from 'metadata/SelectorOptionInput';
import {EntrySearchInputWithItems} from 'notebook/entries/EntrySearchInput';
import ProcessManagementSearchInput from 'processes/ProcessManagementSearchInput';
import {StorableSearchInputWithItems} from 'samples/storables/StorableSearchInput';
import {FilterKey, keyToEmptyFilter} from 'search/SearchConstants';
import StudySearchInput from 'studies/StudySearchInput';
import {FONT_PROXIMA_14} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import {ButtonSize} from 'ui-library/buttons/Button';
import {EntitySuggestion, EntityWithTextAndSuspended} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import {UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import FieldError from 'ui-library/FieldError';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import {DateInput} from 'ui-library/inputs/DatetimeInput';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import ProjectSelector from 'ui-library/ProjectSelector';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {NumericInputChangeEvent, JSONValue} from 'util/Types';

import {SelectorValueInputQuery} from './__generated/SelectorValueInputQuery';
import {TeamLinkValueInputQuery} from './__generated/TeamLinkValueInputQuery';
import {UserLinkValueInputQuery} from './__generated/UserLinkValueInputQuery';
import {ParameterTypeToTSType} from './data/ValueValidators';
import {ParameterTypeToSettingType} from './ParameterConfigSetting';
import {ParameterType} from './ParameterTypes';

// Turn off auto focus on chips/dates so the chip/date menu doesn't appear when opening the modal, which gives nicer UX
const AUTO_FOCUS_PLAIN_TEXT = true;
const AUTO_FOCUS_DATE = false;
const AUTO_FOCUS_CHIP = false;

export function singleValueToArrayValues<T>(id: T | null): Array<T> {
  return id ? [id] : [];
}

function arrayValuesToSingleValue<T>(ids: Array<T>): T | null {
  assertAndContinue(
    ids.length <= 1,
    `Parameters support selecting one or zero values, got ${ids.length} values`
  );
  return ids[0] ?? null;
}

function getCurrentlySelectedStringValues(valueJson: string | Array<string> | null): Array<string> {
  if (Array.isArray(valueJson)) {
    return valueJson;
  }

  return valueJson ? [valueJson] : [];
}

/**
 * Helper HOC that transforms a single and multiple id values into an array of items to pass into a search
 * input component
 */
function withSupportForSingleAndMultipleValues<
  WrappedProps extends {
    selectedItems: Array<Item>;
    onChange(newItems: Array<Item>): void;
    shouldAllowMultiple?: boolean;
  },
>(
  WrappedComponent: React.ComponentType<WrappedProps>
): React.FunctionComponent<
  Omit<WrappedProps, 'selectedItems' | 'onChange' | 'shouldAllowMultiple'> & {
    value: Array<string> | string | null;
    isMulti: boolean;
    onChange(newId: Array<string> | string | null): void;
  }
> {
  return (props) => {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {value, isMulti, onChange, ...otherProps} = props;

    function onChangeWrapper(selections: Array<Item>): void {
      if (!isMulti) {
        const selection = arrayValuesToSingleValue(selections);
        onChange(selection?.id ?? null);
      } else {
        onChange(
          selections.map(({id}) => id).filter((id: string | null | undefined): id is string => id != null)
        );
      }
    }

    return (
      <WrappedComponent
        {...(otherProps as unknown as WrappedProps)}
        hideSelectedResults={true}
        selectedItems={(Array.isArray(value) ? value : singleValueToArrayValues(value)).map((id) => ({
          id,
        }))}
        onChange={onChangeWrapper}
        shouldAllowMultiple={isMulti}
      />
    );
  };
}

export interface InputProps<k extends ParameterType> {
  valueJson: ParameterTypeToTSType[k];
  paramConfigSetting: ParameterTypeToSettingType[k];
  onChange: (newValueJson: JSONValue) => void;
  // if true, will focus on the field so long as the other focus conditions are true
  // (i.e. AUTO_FOCUS_CHIP = true)
  allowAutoFocus: boolean;
  disabled?: boolean;
}

const WrappedBioentitySearchInput = withSupportForSingleAndMultipleValues(BioentitySearchInputWithItems);

function BioentityLinkValueInput(props: InputProps<ParameterType.BIOENTITY_LINK>): JSX.Element {
  return (
    <WrappedBioentitySearchInput
      autoFocus={AUTO_FOCUS_CHIP && props.allowAutoFocus}
      value={props.valueJson}
      isMulti={props.paramConfigSetting.isMulti}
      onChange={props.onChange}
      tagSchemaOrInterfaceId={props.paramConfigSetting.tagSchema?.id}
      disabled={props.disabled}
    />
  );
}

function DateValueInput(props: InputProps<ParameterType.DATE>): JSX.Element {
  return (
    <DateInput
      autoFocus={AUTO_FOCUS_DATE && props.allowAutoFocus}
      placeholder="Date"
      size={Size.MEDIUM}
      className={css(styles.inputHeightFix)}
      // Coerce null to '' for display
      value={props.valueJson || ''}
      onChange={(newDateValue) => {
        // Coerce '' to null for save
        props.onChange(newDateValue !== '' ? newDateValue : null);
      }}
      isDisabled={props.disabled}
    />
  );
}

const WrappedEntrySearchInput = withSupportForSingleAndMultipleValues(EntrySearchInputWithItems);

function EntryLinkValueInput(props: InputProps<ParameterType.ENTRY_LINK>): JSX.Element {
  return (
    <WrappedEntrySearchInput
      autoFocus={AUTO_FOCUS_CHIP && props.allowAutoFocus}
      value={props.valueJson}
      onChange={props.onChange}
      isMulti={props.paramConfigSetting.isMulti}
      disabled={props.disabled}
    />
  );
}

function FolderLinkValueInput(props: InputProps<ParameterType.FOLDER_LINK>): JSX.Element {
  return (
    <FolderSelector
      allowCreateFolder={false}
      value={props.valueJson || ''}
      onChange={(e: {target: {value: string}}) => {
        props.onChange(e.target.value ?? null);
      }}
      showInventory={true}
      showNotebook={true}
      showRegistries={false}
      readOnly={props.disabled}
      level="read"
      enableRedesign={true}
      allowNone={true}
      unsetOnFetchFail={false}
    />
  );
}

function NumberValueInput(props: InputProps<ParameterType.NUMBER>): JSX.Element {
  return (
    <IntermediateValueControlledNumericInput
      className={css(styles.inputHeightFix, styles.numberInputFontSizeFix)}
      focusOnMount={props.allowAutoFocus}
      onChange={(e: NumericInputChangeEvent) => {
        props.onChange(e.target.numericValue ?? (e.target.value !== '' ? e.target.value : null));
      }}
      placeholder="Number"
      value={props.valueJson}
      isDisabled={props.disabled}
    />
  );
}

const WrappedProcessManagementSearchInput = withSupportForSingleAndMultipleValues(
  ProcessManagementSearchInput
);

function ProcessValueInputInner(
  props: InputProps<ParameterType.PROCESS_LINK> & {
    selectedType: ModelTypeConstants.PROCESS | ModelTypeConstants.PROCESS_TASK;
  }
): JSX.Element {
  const processTaskSchemaFilter =
    props.paramConfigSetting.processTaskSchema?.id != null
      ? {
          ...keyToEmptyFilter(FilterKey.SCHEMA),
          value: [props.paramConfigSetting.processTaskSchema.id],
        }
      : null;

  return (
    <WrappedProcessManagementSearchInput
      onChange={props.onChange}
      selectedType={props.selectedType}
      isMulti={props.paramConfigSetting.isMulti}
      value={props.valueJson}
      extraFilters={processTaskSchemaFilter ? [processTaskSchemaFilter] : undefined}
      isReadOnly={props.disabled}
    />
  );
}

function ProcessValueInput(props: InputProps<ParameterType.PROCESS_LINK>): JSX.Element {
  return <ProcessValueInputInner {...props} selectedType={ModelTypeConstants.PROCESS} />;
}

function ProcessTaskValueInput(props: InputProps<ParameterType.PROCESS_LINK>): JSX.Element {
  return <ProcessValueInputInner {...props} selectedType={ModelTypeConstants.PROCESS_TASK} />;
}

function ProjectValueInput(props: InputProps<ParameterType.PROJECT_LINK>): JSX.Element {
  return (
    <ProjectSelector
      allowNone={true}
      buttonSize={ButtonSize.MEDIUM}
      enableRedesign={true}
      level="read"
      onProjectSelected={(projectId: string) => {
        props.onChange(projectId);
      }}
      selectedProjectId={props.valueJson}
      readOnly={props.disabled}
    />
  );
}

export const SELECTOR_VALUE_INPUT_QUERY = gql`
  query SelectorValueInputQuery($id: StrictString!) @skipAudit @engComponent(name: INSIGHTS) {
    schemaFieldSelector(id: $id) {
      ... on SchemaFieldSelectorNode {
        ...SchemaFieldSelectorGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function SelectorValueInput(props: InputProps<ParameterType.SELECTOR>): JSX.Element | null {
  let schemaFieldSelector = null;
  const result = useSkippableBenchlingQuery<SelectorValueInputQuery>(SELECTOR_VALUE_INPUT_QUERY, {
    skip: props.paramConfigSetting.schemaFieldSelector == null,
    variables: {id: props.paramConfigSetting.schemaFieldSelector?.id},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SKIPPED) {
    return <FieldError message="No dropdown is selected." />;
  } else if (isInaccessible(result.data.schemaFieldSelector)) {
    return <FieldError message="Selected dropdown is inaccessible." />;
  } else {
    schemaFieldSelector = result.data.schemaFieldSelector;
  }

  if (schemaFieldSelector.schemaFieldSelectorOptions.length === 0) {
    return <FieldError message="No options are available for the selected dropdown." />;
  } else {
    // If a user has an option selected, but then changes the linked dropdown for the parameter, we can't pass
    // in the previously selected ID, as SelectorOptionInput expects its default value to be an option on the
    // given selector.
    let selectedOptionIds: Array<string>;
    let errorMessage;
    if (props.valueJson == null) {
      selectedOptionIds = [];
    } else {
      const optionIds = new Set(schemaFieldSelector.schemaFieldSelectorOptions.map((option) => option.id));
      const valueJsonAsArray = Array.isArray(props.valueJson) ? props.valueJson : [props.valueJson];
      selectedOptionIds = valueJsonAsArray.filter((value) => optionIds.has(value));

      if (valueJsonAsArray.length - selectedOptionIds.length === 1) {
        const article = valueJsonAsArray.length === 1 ? 'The' : 'A';
        errorMessage = `${article} previously selected option is no longer available.`;
      } else if (valueJsonAsArray.length - selectedOptionIds.length > 1) {
        errorMessage = 'Previously selected options are no longer available.';
      }
    }

    return (
      <>
        <SelectorOptionInput
          defaultValue={{schemaFieldSelectorOptionIds: selectedOptionIds}}
          onChange={({schemaFieldSelectorOptionIds}) => {
            if (!props.paramConfigSetting.isMulti) {
              props.onChange(arrayValuesToSingleValue(schemaFieldSelectorOptionIds));
            } else {
              props.onChange(schemaFieldSelectorOptionIds);
            }
          }}
          schemaFieldSelector={schemaFieldSelector}
          shouldAllowMultiple={props.paramConfigSetting.isMulti}
          disabled={props.disabled}
        />
        {errorMessage != null && <FieldError message={errorMessage} />}
      </>
    );
  }
}

const WrappedStorableSearchInput = withSupportForSingleAndMultipleValues(StorableSearchInputWithItems);

function StorableLinkValueInput(props: InputProps<ParameterType.STORABLE_LINK>): JSX.Element {
  return (
    <WrappedStorableSearchInput
      autoFocus={AUTO_FOCUS_CHIP && props.allowAutoFocus}
      isMulti={props.paramConfigSetting.isMulti}
      onChange={props.onChange}
      storableSchemaId={props.paramConfigSetting.storableSchema?.id}
      value={props.valueJson}
      disabled={props.disabled}
    />
  );
}

function StringValueInput(props: InputProps<ParameterType.STRING>): JSX.Element {
  return (
    <TextInput
      autoFocus={AUTO_FOCUS_PLAIN_TEXT && props.allowAutoFocus}
      // Coerce null to '' for display
      value={props.valueJson || ''}
      className={css(styles.inputHeightFix)}
      size={Size.MEDIUM}
      onChange={(e) => {
        const newStringValue = e.target.value;
        // Coerce '' to null for save
        props.onChange(newStringValue !== '' ? newStringValue : null);
      }}
      placeholder="Text"
      {...getTestProps({component: 'AnalyticsParameterValueModal', element: 'stringValueInput'})}
      disabled={props.disabled}
    />
  );
}

const WrappedStudyLinkSearchInput = withSupportForSingleAndMultipleValues(StudySearchInput);

function StudyLinkValueInput(props: InputProps<ParameterType.STUDY_LINK>): JSX.Element {
  return (
    <WrappedStudyLinkSearchInput
      autoFocus={AUTO_FOCUS_CHIP && props.allowAutoFocus}
      value={props.valueJson}
      isMulti={props.paramConfigSetting.isMulti}
      studySchemaId={props.paramConfigSetting.studySchema?.id}
      onChange={props.onChange}
      disabled={props.disabled}
    />
  );
}

export const TEAM_LINK_VALUE_INPUT_QUERY = gql`
  query TeamLinkValueInputQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: INSIGHTS) {
    parties(ids: $ids) {
      # For some reason, without querying for PartyInterface, tests become flaky, sometimes using the correct
      # GraphQL mocks, and sometimes generating random data.
      ... on PartyInterface {
        id
      }
      ... on TeamNode {
        id
        handle
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function TeamLinkValueInput(props: InputProps<ParameterType.TEAM_LINK>): JSX.Element | null {
  const [teamDataFromSearch, setTeamDataFromSearch] = useState<Array<EntitySuggestion> | null>(null);
  const result = useSkippableBenchlingQuery<TeamLinkValueInputQuery>(TEAM_LINK_VALUE_INPUT_QUERY, {
    variables: {ids: getCurrentlySelectedStringValues(props.valueJson)},
    skip: teamDataFromSearch != null || props.valueJson == null,
  });

  function handleOnChange(teams: Array<EntityWithTextAndSuspended>): void {
    setTeamDataFromSearch(teams);

    if (!props.paramConfigSetting.isMulti) {
      const team = arrayValuesToSingleValue(teams);
      props.onChange(team?.id ?? null);
    } else {
      props.onChange(teams.map(({id}) => id));
    }
  }

  function filterOptions(
    tag: string,
    suggestions: Array<EntityWithTextAndSuspended>
  ): Array<EntityWithTextAndSuspended> {
    const selectedIds = new Set(Array.isArray(props.valueJson) ? props.valueJson : [props.valueJson]);

    return suggestions.filter((suggestion) => !selectedIds.has(suggestion.id));
  }

  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber className={css(styles.throbber)} />;
  } else {
    let teamData: Array<EntitySuggestion> | null = null;
    if (teamDataFromSearch) {
      teamData = teamDataFromSearch;
    } else if (result.data) {
      const teamDataFromGQLQuery = result.data.parties.filter((data) => !isInaccessible(data));
      assert(
        teamDataFromGQLQuery.every((data) => data.__typename === 'TeamNode'),
        'Got non-team from TeamLinkValueInputQuery'
      );
      teamData = teamDataFromGQLQuery;
    }

    return (
      <EntityInputWithSearch
        allowTeams={true}
        allowEmails={false}
        allowBenchlingApps={false}
        allowOrganizations={false}
        autoFocus={AUTO_FOCUS_CHIP && props.allowAutoFocus}
        enableNewEntityInput={true}
        filterOptions={filterOptions}
        onChange={handleOnChange}
        shouldAllowMultiple={props.paramConfigSetting.isMulti}
        size={TagsInputSize.MEDIUM}
        userSearchType={UserSearchType.None}
        value={teamData ?? []}
        disabled={props.disabled}
      />
    );
  }
}

export const USER_LINK_VALUE_INPUT_QUERY = gql`
  query UserLinkValueInputQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: INSIGHTS) {
    parties(ids: $ids) {
      # For some reason, without querying for PartyInterface, tests become flaky, sometimes using the correct
      # GraphQL mocks, and sometimes generating random data.
      ... on PartyInterface {
        id
      }
      ... on UserNode {
        id
        handle
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function UserLinkValueInput(props: InputProps<ParameterType.USER_LINK>): JSX.Element | null {
  // We can use userDataFromSearch for rendering EntityInputWithSearch except on the initial render, when
  // the only data we have is the user ID. In that situation, we need to use UserLinkValueQuery to get the
  // user's name and handle to be passed into EntityInputWithSearch.
  const [userDataFromSearch, setUserDataFromSearch] = useState<Array<EntitySuggestion> | null>(null);
  const result = useSkippableBenchlingQuery<UserLinkValueInputQuery>(USER_LINK_VALUE_INPUT_QUERY, {
    variables: {ids: getCurrentlySelectedStringValues(props.valueJson)},
    skip: userDataFromSearch != null || props.valueJson == null,
  });

  function handleOnChange(users: Array<EntityWithTextAndSuspended>): void {
    setUserDataFromSearch(users);

    if (!props.paramConfigSetting.isMulti) {
      const user = arrayValuesToSingleValue(users);
      props.onChange(user?.id ?? null);
    } else {
      props.onChange(users.map(({id}) => id));
    }
  }

  function filterOptions(
    tag: string,
    suggestions: Array<EntityWithTextAndSuspended>
  ): Array<EntityWithTextAndSuspended> {
    const selectedIds = new Set(Array.isArray(props.valueJson) ? props.valueJson : [props.valueJson]);

    return suggestions.filter((suggestion) => !selectedIds.has(suggestion.id));
  }

  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber className={css(styles.throbber)} />;
  } else {
    let userData: Array<EntitySuggestion> | null = null;
    if (userDataFromSearch) {
      userData = userDataFromSearch;
    } else if (result.data) {
      const userDataFromGQLQuery = result.data.parties.filter((data) => !isInaccessible(data));
      assert(
        userDataFromGQLQuery.every((data) => data.__typename === 'UserNode'),
        'Got non-user from UserLinkValueInputQuery'
      );
      userData = userDataFromGQLQuery;
    }

    return (
      <EntityInputWithSearch
        // These three props ensure only user entities will be suggested to or selected by the user.
        allowTeams={false}
        allowBenchlingApps={false}
        allowOrganizations={false}
        autoFocus={AUTO_FOCUS_CHIP && props.allowAutoFocus}
        enableNewEntityInput={true}
        filterOptions={filterOptions}
        onChange={handleOnChange}
        shouldAllowMultiple={props.paramConfigSetting.isMulti}
        size={TagsInputSize.MEDIUM}
        value={userData ?? []}
        disabled={props.disabled}
      />
    );
  }
}

type ValueInputComponentType<T extends ParameterType> = React.FC<InputProps<T>>;

export const PARAMETER_TYPE_TO_VALUE_INPUT: {
  [k in ParameterType]: ValueInputComponentType<k>;
} = {
  [ParameterType.BIOENTITY_LINK]: BioentityLinkValueInput,
  [ParameterType.DATE]: DateValueInput,
  [ParameterType.ENTRY_LINK]: EntryLinkValueInput,
  [ParameterType.FOLDER_LINK]: FolderLinkValueInput,
  [ParameterType.NUMBER]: NumberValueInput,
  [ParameterType.PROCESS_LINK]: ProcessValueInput,
  [ParameterType.PROCESS_TASK_LINK]: ProcessTaskValueInput,
  [ParameterType.PROJECT_LINK]: ProjectValueInput,
  [ParameterType.SELECTOR]: SelectorValueInput,
  [ParameterType.STORABLE_LINK]: StorableLinkValueInput,
  [ParameterType.STRING]: StringValueInput,
  [ParameterType.STUDY_LINK]: StudyLinkValueInput,
  [ParameterType.TEAM_LINK]: TeamLinkValueInput,
  [ParameterType.USER_LINK]: UserLinkValueInput,
};

export interface AnalyticsParameterValueEditorProps<T extends ParameterType> {
  paramType: ParameterType;
  inputProps: InputProps<T>;
}

export default function AnalyticsParameterValueEditor<T extends ParameterType>(
  props: AnalyticsParameterValueEditorProps<T>
): JSX.Element {
  const Editor = PARAMETER_TYPE_TO_VALUE_INPUT[props.paramType] as ValueInputComponentType<T>;

  return <Editor {...props.inputProps} />;
}

const styles = StyleSheet.create({
  inputHeightFix: {
    height: 36,
  },
  numberInputFontSizeFix: {
    ...FONT_PROXIMA_14,
  },
  throbber: {
    // Using standard 10px margin on top and bottom makes the loading throbber 1px taller than the inputs
    paddingTop: 9,
    paddingBottom: 10,
  },
});
